import { Box, Button, Modal } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { useState } from "react";
import CustomAxios from "../utils/CustomAxios";
import { TranslateText } from "../utils/googletranslator";
import { updateMpinRequirements, updateMpinOptional } from "../redux/user_reducer";
import {
  updateSnackBarMessage,
  updateSnackBarOpen,
  updateSnackBarSeverity,
} from "../redux/snackbar";

const isStrictRun = (digits) => {
  if (!digits || digits.length < 2) return false;
  const diffs = [];
  for (let i = 1; i < digits.length; i += 1) {
    const cur = Number(digits[i]);
    const prev = Number(digits[i - 1]);
    if (Number.isNaN(cur) || Number.isNaN(prev)) {
      return false;
    }
    diffs.push(cur - prev);
  }
  const allPlus = diffs.every((d) => d === 1);
  const allMinus = diffs.every((d) => d === -1);
  return allPlus || allMinus;
};

const validateMpinClient = (mpin) => {
  if (!/^[0-9]{6}$/.test(mpin)) {
    return "MPIN must be exactly 6 digits.";
  }

  // All digits identical, e.g. 000000, 777777
  if (new Set(mpin.split("")).size === 1) {
    return "MPIN is too easy to guess. Please choose a more complex combination.";
  }

  // Full 6‑digit run
  if (isStrictRun(mpin)) {
    return "MPIN cannot be a simple ascending or descending sequence.";
  }

  // Any 4‑digit run inside the 6 digits
  for (let i = 0; i <= 2; i += 1) {
    const window = mpin.slice(i, i + 4);
    if (isStrictRun(window)) {
      return "MPIN cannot contain simple ascending or descending runs like 1234 or 6543.";
    }
  }

  return "";
};

const MpinOverlay = () => {
  const dispatch = useDispatch();
  const requirements = useSelector((state) => state.user.mpinRequirements);
  const mpinOptional = useSelector((state) => state.user.mpinOptional);
  const userId = useSelector((state) => state.user.id);
  const [mpin, setMpin] = useState("");
  const [confirmMpin, setConfirmMpin] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const open =
    requirements.mpin_setup_required ||
    requirements.mpin_change_required ||
    requirements.mpin_force_reset_required;

  const handleCloseOptional = () => {
    if (!mpinOptional) {
      return;
    }
    dispatch(
      updateMpinRequirements({
        mpin_setup_required: false,
        mpin_change_required: false,
        mpin_force_reset_required: false,
      })
    );
    dispatch(updateMpinOptional(false));
  };

  const handleSubmit = async () => {
    setError("");
    if (!mpin || !confirmMpin) {
      setError("Please enter and confirm your MPIN.");
      return;
    }
    if (mpin !== confirmMpin) {
      setError("MPIN and confirmation do not match.");
      return;
    }
    const clientError = validateMpinClient(mpin);
    if (clientError) {
      setError(clientError);
      return;
    }
    setLoading(true);
    try {
      // mpin_force_reset_required means user already has MPIN but needs to reset it, so use change endpoint
      const url =
        requirements.mpin_setup_required
          ? "mpin/set/"
          : "mpin/change/";
      const payload =
        requirements.mpin_setup_required
          ? { mpin, user_id: userId }
          : { mpin, user_id: userId };
      const { data } = await CustomAxios.post(url, payload);
      dispatch(
        updateMpinRequirements(
          data.requirements || {
            mpin_setup_required: false,
            mpin_change_required: false,
            mpin_force_reset_required: false,
          }
        )
      );
      dispatch(updateMpinOptional(false));
      dispatch(
        updateSnackBarMessage(
          data?.detail || "MPIN updated successfully."
        )
      );
      dispatch(updateSnackBarSeverity("success"));
      dispatch(updateSnackBarOpen(true));
      setMpin("");
      setConfirmMpin("");
    } catch (err) {
      const errorMsg =
        err.response?.data?.detail ||
        "Failed to update MPIN. Please try again.";
      setError(
        errorMsg
      );
      dispatch(updateSnackBarMessage(errorMsg));
      dispatch(updateSnackBarSeverity("error"));
      dispatch(updateSnackBarOpen(true));
    } finally {
      setLoading(false);
    }
  };

  if (!open) {
    return null;
  }

  return (
    <Modal open={open}>
      <Box
        sx={{
          position: "fixed",
          inset: 0,
          bgcolor: "rgba(0,0,0,0.65)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 2000,
        }}
      >
        <Box
          sx={{
            width: 360,
            bgcolor: "#fff",
            borderRadius: "12px",
            p: 3,
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          <TranslateText sx={{ fontWeight: 600, fontSize: 18 }}>
            {requirements.mpin_setup_required
              ? "Set your login MPIN"
              : "Update your login MPIN"}
          </TranslateText>
          <TranslateText sx={{ fontSize: 14, color: "text.secondary" }}>
            Your 6-digit MPIN keeps your account secure. Please choose a
            combination that is not easy to guess.
          </TranslateText>
          <input
            type="password"
            inputMode="numeric"
            maxLength={6}
            value={mpin}
            onChange={(e) =>
              setMpin(e.target.value.replace(/[^0-9]/g, "").slice(0, 6))
            }
            style={{
              height: 40,
              padding: "0 12px",
              borderRadius: 8,
              border: "1px solid #CBD2E0",
              fontSize: 18,
              letterSpacing: 4,
            }}
            placeholder="New MPIN"
          />
          <input
            type="password"
            inputMode="numeric"
            maxLength={6}
            value={confirmMpin}
            onChange={(e) =>
              setConfirmMpin(
                e.target.value.replace(/[^0-9]/g, "").slice(0, 6)
              )
            }
            style={{
              height: 40,
              padding: "0 12px",
              borderRadius: 8,
              border: "1px solid #CBD2E0",
              fontSize: 18,
              letterSpacing: 4,
            }}
            placeholder="Confirm MPIN"
          />
          {error && (
            <TranslateText sx={{ fontSize: 12, color: "error.main" }}>
              {error}
            </TranslateText>
          )}
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
            {mpinOptional && (
              <Button onClick={handleCloseOptional} color="inherit">
                <TranslateText>Cancel</TranslateText>
              </Button>
            )}
            <Button variant="contained" onClick={handleSubmit} disabled={loading}>
              <TranslateText>{loading ? "Saving..." : "Save MPIN"}</TranslateText>
            </Button>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

export default MpinOverlay;

